import React, { useState } from "react";
import { MdErrorOutline, MdRefresh, MdSync } from "react-icons/md";
import { toast } from "react-toastify";
import api from "../../../../services/axios";
import { Billing, EmitInvoiceResult, siimpResultadoMensagem } from "../../../../interfaces/Financial";

/**
 * Onde a cobrança está no SIIMP (NFS-e → fatura → baixa). Com erro, mostra o motivo e o botão
 * "Reprocessar", que retoma do passo em que parou — nunca recria o que já existe lá.
 * Não renderiza nada para cobrança que não passou pelo SIIMP.
 */
const SiimpStatus: React.FC<{ billing: Billing; onChange: () => void }> = ({ billing, onChange }) => {
  const [loading, setLoading] = useState(false);

  if (!billing.siimpEtapa && !billing.siimpErro) return null;

  const reprocessar = async () => {
    setLoading(true);
    try {
      const res = await api.post<EmitInvoiceResult>(`/Financial/Siimp/Reprocessar/${billing.billingId}`);
      const { ok, texto } = siimpResultadoMensagem(res.data);
      if (ok) toast.success(texto, { position: "top-right", autoClose: 6000 });
      else toast.error(texto, { position: "top-right", autoClose: 10000 });
    } catch (err: any) {
      const m = err?.response?.data;
      const conflito = err?.response?.status === 409;
      const padrao = conflito ? "Esta cobrança está sendo processada agora." : "Falha ao reprocessar no SIIMP.";
      toast.error(typeof m === "string" && m ? m : padrao, { position: "top-right", autoClose: 6000 });
    } finally {
      setLoading(false);
      onChange();
    }
  };

  if (billing.siimpErro) {
    return (
      <div className="mt-1.5 rounded-lg bg-red-50 px-2.5 py-1.5 text-[11px] text-red-700 dark:bg-red-900/20 dark:text-red-300">
        <div className="flex items-start gap-1.5">
          <MdErrorOutline className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="break-words">SIIMP: {billing.siimpErro}</span>
        </div>
        <button
          onClick={reprocessar}
          disabled={loading}
          className="mt-1 inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-0.5 font-semibold transition-colors hover:bg-red-100 disabled:opacity-50 dark:border-red-400/30 dark:hover:bg-red-900/40"
        >
          <MdRefresh className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
          {loading ? "Reprocessando…" : "Reprocessar"}
        </button>
      </div>
    );
  }

  return (
    <span className="mt-1.5 inline-flex items-center gap-1 rounded-lg bg-dv-blue-50 px-2 py-0.5 text-[11px] font-medium text-dv-blue-700 dark:bg-dv-blue-900/30 dark:text-dv-cyan-300">
      <MdSync className="h-3.5 w-3.5" aria-hidden="true" />
      {billing.siimpEtapaNome}
      {billing.siimpFaturaNumero && <> · fatura {billing.siimpFaturaNumero}</>}
    </span>
  );
};

export default SiimpStatus;
